import { useEffect } from 'react';

import { defineMessages } from 'react-intl';
import { useSelector } from 'react-redux';

import { Modal, ModalTransition } from '@atlaskit/onboarding';

import { useCodeReviewerRepositorySetting } from 'src/components/rovo-dev-agents/src/hooks';
import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';
import {
  getCurrentRepositoryFullSlug,
  getRepositoryAccessLevel,
} from 'src/selectors/repository-selectors';
import {
  publishScreenEvent,
  publishUiEvent,
} from 'src/utils/analytics/publish';

import aiCodeReviewerOnboardingImage from './code-reviewer-repo-onboarding-img.png';

const messages = defineMessages({
  aiCodeReviewerOnboardingHeader: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.header',
    description: 'Header for AI Code Reviewer onboarding',
    defaultMessage: 'Activate AI-powered code reviews for faster PR feedback',
  },
  aiCodeReviewerOnboardingBody: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.body',
    description: 'Body for AI Code Reviewer onboarding',
    defaultMessage:
      'Automatically check pull requests to identify quality, security, and/or performance issues in your code before other engineers review it.',
  },
  takeMeThere: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.button.takeMeThere',
    description:
      'Text to display on the button to navigate to the AI Code Reviewer page',
    defaultMessage: 'Activate in this repository',
  },
  dismiss: {
    id: 'frontbucket.repository.aiCodeReviewerOnboarding.button.dismiss',
    description: 'Text to display on the button to dismiss the onboarding',
    defaultMessage: 'Remind me later',
  },
});

export const DevAIRepoAdminsOnboarding = (): JSX.Element | null => {
  const intl = useIntl();
  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);
  const isRepoAdmin = repositoryAccessLevel === 'admin';
  const [codeReviewerRepoOnboardingSeen, updatePref, isLoading] = usePref(
    'ai-code-reviewer-repo-onboarding-seen'
  );
  const repoSlug = useSelector(getCurrentRepositoryFullSlug);
  const { isEnabledAtRepository, isEnabledAtWorkspace } =
    useCodeReviewerRepositorySetting();
  let shouldNotShowModal =
    codeReviewerRepoOnboardingSeen ||
    isLoading ||
    !isRepoAdmin ||
    !isEnabledAtWorkspace ||
    isEnabledAtRepository;

  useEffect(() => {
    if (!shouldNotShowModal) {
      publishScreenEvent('codeReviewerRepoOnboardingModal');
    }
  }, [shouldNotShowModal]);

  if (shouldNotShowModal) {
    return null;
  }

  const handleRedirectToSettings = async () => {
    await updatePref('true');
    await publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'codeReviewerRepoOnboardingCtaButton',
      source: 'codeReviewerRepoOnboardingModalScreen',
    });
    window.location.href = `/${repoSlug}/admin/code-reviewer/`;
  };

  const handleDismiss = () => {
    updatePref('true');
    shouldNotShowModal = true;
  };

  const renderModal = () => {
    return (
      <Modal
        experimental_shouldShowPrimaryButtonOnRight
        actions={[
          {
            onClick: handleRedirectToSettings,
            text: intl.formatMessage(messages.takeMeThere),
          },
          {
            onClick: () => handleDismiss(),
            text: intl.formatMessage(messages.dismiss),
          },
        ]}
        heading={intl.formatMessage(messages.aiCodeReviewerOnboardingHeader)}
        image={aiCodeReviewerOnboardingImage}
        key="ai-repo-code-reviewer-onboarding"
      >
        {intl.formatMessage(messages.aiCodeReviewerOnboardingBody)}
      </Modal>
    );
  };

  return <ModalTransition>{renderModal()}</ModalTransition>;
};
